import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import Tooltip from '@mui/material/Tooltip';
import { useConceptGate } from '../../../context/ConceptGateContext';
import { RuleType } from '../../../api/conceptgate';
import HardcodedRuleForm from './config/HardcodedRuleForm';
import LlmRuleForm from './config/LlmRuleForm';

import './RuleConfigModal.css';

const RuleConfigModal = () => {
  const {
    configModalOpen,
    selectedRuleForConfig,
    closeConfigModal,
    updateRuleConfig,

    toggleUserRuleEnabled,
    removeUserRule,
  } = useConceptGate();

  const [localConfig, setLocalConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Initialize from current config when modal opens
  useEffect(() => {
    if (selectedRuleForConfig) {
      const { userRule, libraryRule } = selectedRuleForConfig;

      // Parse default config from library rule or user rule
      let defaultConfig = {};
      const defaultConfigSource = libraryRule?.defaultConfigJson || userRule?.defaultConfigJson;

      if (defaultConfigSource) {
        try {
          defaultConfig = typeof defaultConfigSource === 'string'
            ? JSON.parse(defaultConfigSource)
            : defaultConfigSource;
        } catch (e) {
          // Ignore parse errors
        }
      }

      // Parse custom config from user rule
      let customConfig = {};
      if (userRule.customConfigJson) {
        try {
          customConfig = typeof userRule.customConfigJson === 'string'
            ? JSON.parse(userRule.customConfigJson)
            : userRule.customConfigJson;
        } catch (e) {
          // Ignore parse errors
        }
      }

      setLocalConfig({ ...defaultConfig, ...customConfig });
      setHasChanges(false);
    }
  }, [selectedRuleForConfig]);

  const handleConfigChange = (newConfig) => {
    setLocalConfig(newConfig);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedRuleForConfig) return;

    setSaving(true);
    await updateRuleConfig(selectedRuleForConfig.userRule.id, localConfig);
    setSaving(false);
    closeConfigModal();
  };



  const handleToggleEnabled = async () => {
    if (!selectedRuleForConfig) return;

    setToggling(true);
    await toggleUserRuleEnabled(selectedRuleForConfig.userRule.id, selectedRuleForConfig.userRule.enabled);
    setToggling(false);
    // Update local state to reflect the change
    selectedRuleForConfig.userRule.enabled = !selectedRuleForConfig.userRule.enabled;
  };

  const handleDelete = async () => {
    if (!selectedRuleForConfig) return;
    const { userRule, libraryRule } = selectedRuleForConfig;
    // Remove with undo support (uses context's undo toast)
    await removeUserRule(userRule.id, libraryRule?.name || userRule?.name);
    closeConfigModal();
  };

  if (!configModalOpen || !selectedRuleForConfig) return null;

  const { userRule, libraryRule } = selectedRuleForConfig;
  const effectiveRuleType = userRule?.ruleType || libraryRule?.ruleType;
  const isLlmRule = effectiveRuleType === RuleType.LLM_AGENT || effectiveRuleType === 'LLM_AGENT';

  const isEnabled = userRule?.enabled !== false;

  // Get current name/description from config or userRule (fallback to libraryRule if available)
  const currentName = localConfig.name ?? userRule?.name ?? libraryRule?.name ?? '';
  const currentDescription = localConfig.description ?? userRule?.description ?? libraryRule?.description ?? '';

  const handleNameChange = (e) => {
    setLocalConfig({ ...localConfig, name: e.target.value });
    setHasChanges(true);
  };

  const handleDescriptionChange = (e) => {
    setLocalConfig({ ...localConfig, description: e.target.value });
    setHasChanges(true);
  };

  return (
    <div className="rule-config-modal-overlay" onClick={closeConfigModal}>
      <div className="rule-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Configure Rule</h2>
          <div className="modal-header-actions">
            <Tooltip title="Remove rule" arrow>
              <button className="modal-delete-btn" onClick={handleDelete}>
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </Tooltip>
            <button className="modal-close-btn" onClick={closeConfigModal}>
              <CloseIcon />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {/* Editable Name */}
          <div className="form-field">
            <label className="form-label">Rule Name</label>
            <input
              type="text"
              value={currentName}
              onChange={handleNameChange}
              className="form-input"
              placeholder="Enter rule name..."
              disabled
            />
          </div>

          {/* Editable Description */}
          <div className="form-field">
            <label className="form-label">Description</label>
            <textarea
              value={currentDescription}
              onChange={handleDescriptionChange}
              className="form-textarea"
              rows={2}
              placeholder="Enter rule description..."
              disabled
            />
          </div>

          <div className="rule-meta">
            <span className={`rule-type ${isLlmRule ? 'llm' : 'static'}`}>
              {isLlmRule ? 'AI Rule' : 'Static Rule'}
            </span>
            <span className="rule-category">{userRule?.category || libraryRule?.category || 'General'}</span>
          </div>

          {/* Enable/Disable Toggle */}
          <div className="rule-status-section">
            <div className="status-label">Status</div>
            <button
              className={`status-toggle ${isEnabled ? 'enabled' : 'disabled'}`}
              onClick={handleToggleEnabled}
              disabled={toggling}
            >
              {isEnabled ? (
                <>
                  <ToggleOnIcon className="toggle-icon" />
                  <span>Enabled</span>
                </>
              ) : (
                <>
                  <ToggleOffIcon className="toggle-icon" />
                  <span>Disabled</span>
                </>
              )}
            </button>
          </div>

          <div className="config-divider" />

          {isLlmRule ? (
            <LlmRuleForm
              libraryRule={libraryRule}
              userRule={userRule}
              config={localConfig}
              onConfigChange={handleConfigChange}
            />
          ) : (
            <HardcodedRuleForm
              libraryRule={libraryRule}
              userRule={userRule}
              config={localConfig}
              onConfigChange={handleConfigChange}
            />
          )}



        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={closeConfigModal}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RuleConfigModal;
